import { Injectable, InjectionToken, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { AssistantFile } from '@core/models/assistant/assistant.model';
import { downloadBlob } from '@core/utils/download.utils';

import { assistantMarkdown } from '../assistant-message/assistant-markdown';
import { ChartType, renderChart } from './assistant-report-charts';
import { sanitizeReportBody } from './assistant-report-sanitizer';
import {
  ReportDocument,
  ReportLegendRole,
  buildReportHtml
} from './assistant-report.utils';

/** Loads the embedded font faces, kept in a lazy chunk because they weigh about 230 KB. */
export const REPORT_FONT_LOADER = new InjectionToken<() => Promise<string>>(
  'REPORT_FONT_LOADER',
  {
    providedIn: 'root',
    factory: () => () =>
      import('./assistant-report-fonts.generated').then((m) => m.REPORT_FONT_FACES)
  }
);

const MIME = {
  html: 'text/html;charset=utf-8',
  markdown: 'text/markdown;charset=utf-8'
} as const;

const CHART_TYPES: readonly string[] = [
  'change',
  'bars',
  'stacked',
  'meter'
] satisfies ChartType[];

const ROLE_OF_DATA_ROLE: Record<string, ReportLegendRole> = {
  better: 'good',
  worse: 'bad',
  review: 'review'
};
const LEGEND_ORDER: ReportLegendRole[] = ['brand', 'parent', 'good', 'bad', 'review'];
// Each of these only means something against the plan or period named by compared_to.
const COMPARISON_ROLES = new Set<ReportLegendRole>(['brand', 'parent', 'good', 'bad']);
const CHART_FILL = /fill="var\(--(brand|brand-2|parent|good|bad|review)\)"/g;

@Injectable({ providedIn: 'root' })
export class AssistantReportService {
  private readonly translate = inject(TranslateService);
  private readonly loadFonts = inject(REPORT_FONT_LOADER);
  private fontFaces: Promise<string> | null = null;

  /** The standalone report document for a file whose content is still stored. */
  async document(file: AssistantFile): Promise<string> {
    const fontFaces = await this.loadFontFaces();
    const content = file.content ?? '';
    const lang = this.translate.getCurrentLang() ?? 'en';
    const comparedTo = file.comparedTo ?? null;
    const html =
      file.format === 'html' ? content : (assistantMarkdown.parse(content) as string);
    const body = new DOMParser().parseFromString(
      sanitizeReportBody(html),
      'text/html'
    ).body;
    const roles = new Set<ReportLegendRole>();
    this.drawCharts(body, comparedTo, lang, roles);
    for (const el of Array.from(body.querySelectorAll('[data-role]'))) {
      const role = ROLE_OF_DATA_ROLE[el.getAttribute('data-role') ?? ''];
      if (role) roles.add(role);
    }
    for (const table of Array.from(body.querySelectorAll('table:not(.chart-data)'))) {
      const scroller = body.ownerDocument.createElement('div');
      scroller.className = 'report-scroll';
      table.replaceWith(scroller);
      scroller.append(table);
    }
    const doc: ReportDocument = {
      title: file.title,
      bodyHtml: body.innerHTML,
      generatedAt: this.translate.instant('assistant.files.generated', {
        date: new Intl.DateTimeFormat(lang, {
          dateStyle: 'long',
          timeStyle: 'short'
        }).format(file.createdAt)
      }),
      footer: this.translate.instant('assistant.files.footer'),
      provenance: this.translate.instant('assistant.files.provenance'),
      lang,
      summary: file.summary ?? null,
      subject: file.subject ?? null,
      meta: file.meta ?? [],
      legend: this.legend(roles, comparedTo, lang),
      fontFaces,
      tableLabels: {
        show: this.translate.instant('assistant.files.table.show'),
        all: this.translate.instant('assistant.files.table.all'),
        search: this.translate.instant('assistant.files.table.search'),
        count: this.translate.instant('assistant.files.table.count', {
          shown: '{shown}',
          total: '{total}'
        })
      }
    };
    return buildReportHtml(doc);
  }

  async download(file: AssistantFile): Promise<void> {
    if (file.content === null) return;
    const body = file.format === 'html' ? await this.document(file) : file.content;
    downloadBlob(new Blob([body], { type: MIME[file.format] }), file.filename);
  }

  // A report without its fonts falls back to system fonts, which beats no report; a failed chunk load is
  // forgotten so the next report tries again.
  private loadFontFaces(): Promise<string> {
    this.fontFaces ??= this.loadFonts().catch(() => {
      this.fontFaces = null;
      return '';
    });
    return this.fontFaces;
  }

  private drawCharts(
    body: HTMLElement,
    comparedTo: string | null,
    lang: string,
    roles: Set<ReportLegendRole>
  ): void {
    for (const figure of Array.from(body.querySelectorAll('figure[data-chart]'))) {
      const type = figure.getAttribute('data-chart') ?? '';
      const table = figure.querySelector('table');
      const svg =
        table && CHART_TYPES.includes(type)
          ? renderChart(type as ChartType, table, { comparedTo, lang })
          : null;
      if (table && svg) {
        table.insertAdjacentHTML('beforebegin', svg);
        table.classList.add('chart-data');
        for (const [, token] of svg.matchAll(CHART_FILL)) {
          roles.add(token === 'brand-2' ? 'brand' : (token as ReportLegendRole));
        }
      } else {
        const note = body.ownerDocument.createElement('p');
        note.className = 'chart-fallback';
        note.textContent = this.translate.instant('assistant.files.chart_unavailable');
        figure.append(note);
      }
    }
  }

  private legend(
    roles: Set<ReportLegendRole>,
    comparedTo: string | null,
    lang: string
  ): ReportDocument['legend'] {
    const name = comparedTo ?? '';
    const labels: Record<ReportLegendRole, () => string> = {
      brand: () => this.translate.instant('assistant.files.legend.brand'),
      parent: () => name.charAt(0).toLocaleUpperCase(lang) + name.slice(1),
      good: () => this.translate.instant('assistant.files.legend.better', { name }),
      bad: () => this.translate.instant('assistant.files.legend.worse', { name }),
      review: () => this.translate.instant('assistant.files.legend.review')
    };
    return LEGEND_ORDER.filter(
      (role) => roles.has(role) && (comparedTo || !COMPARISON_ROLES.has(role))
    ).map((role) => ({ role, label: labels[role]() }));
  }
}
